import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';

const STORE_API_URL = process.env.REACT_APP_API_URL;

const BulkEditModal = ({ show, onHide, selectedCRTs, onSuccess }) => {
  const { token } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    rgb_modifiable: '',
    component_modifiable: '',
    svideo_modifiable: '',
    composite_modifiable: '',
    chassis: [],
    chroma: [],
    osd: [],
    mod_boards: [],
    mods: '',
    gallery_images: false,
    manual_links: false,
    manual_links_data: null,
    shared_documents: false,
    shared_documents_data: null,
    tubes: false,
    tubes_data: null,
    // RGB mod fields
    rgb_mod_link: '',
    rgb_mod_notes: '',
    // RGB MUX calculator fields
    rgb_mux_calculator_enabled: '',
    rgb_direct_injection: '',
    chassis_stereo: '',
    chassis_inline_resistors: '',
    chassis_ground_resistors: '',
    chassis_blanking_diode: '',
    chassis_r_g_b_diodes: '',
    chassis_inline_jungle_pin_numbers: '',
    chassis_inline_osd_pin_numbers: '',
    chassis_inline_resistor_numbers: '',
    chassis_ground_resistor_numbers: '',
    chassis_inline_blanking_resistor_value: '',
    chassis_blanking_ground_resistor_value: '',
    chassis_capacitor_numbers: '',
    chassis_capacitor_value: '',
    chassis_change_caps_label: '',
    chassis_blanking_diode_position_before_ground: '',
    chassis_blanking_diode_added: '',
    chassis_blanking_inductor: '',
    chassis_blanking_inductor_number: '',
    chassis_r_g_b_diodes_added: '',
    mux_termination_resistors: '',
    mux_rgb_input_resistors: '',
    mux_rgb_input_capacitors: '',
    mux_blanking_ground_resistor: '',
    mux_blanking_inline_resistor: '',
    mux_audio_resistors: '',
    mux_blanking_diode: '',
    analog_chroma_vpp: '',
    mux_transistor: '',
    mux_transistor_base_resistor: '',
    confirmed: false
  });

  const [options, setOptions] = useState({});
  const [availableModBoards, setAvailableModBoards] = useState([]);
  const [showRgbMuxSection, setShowRgbMuxSection] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [selectedCrtModel, setSelectedCrtModel] = useState(null);
  const [modelInput, setModelInput] = useState('');
  
  useEffect(() => {
    if (show) {
      setFormData({
        rgb_modifiable: '',
        component_modifiable: '',
        svideo_modifiable: '',
        composite_modifiable: '',
        chassis: [],
        chroma: [],
        osd: [],
        mod_boards: [],
        mods: '',
        gallery_images: false,
        manual_links: false,
        manual_links_data: null,
        shared_documents: false,
        shared_documents_data: null,
        tubes: false,
        tubes_data: null,
        // RGB mod fields
        rgb_mod_link: '',
        rgb_mod_notes: '',
        // RGB MUX calculator fields
        rgb_mux_calculator_enabled: '',
        rgb_direct_injection: '',
        chassis_stereo: '',
        chassis_inline_resistors: '',
        chassis_ground_resistors: '',
        chassis_blanking_diode: '',
        chassis_r_g_b_diodes: '',
        chassis_inline_jungle_pin_numbers: '',
        chassis_inline_osd_pin_numbers: '',
        chassis_inline_resistor_numbers: '',
        chassis_ground_resistor_numbers: '',
        chassis_inline_blanking_resistor_value: '',
        chassis_blanking_ground_resistor_value: '',
        chassis_capacitor_numbers: '',
        chassis_capacitor_value: '',
        chassis_change_caps_label: '',
        chassis_blanking_diode_position_before_ground: '',
        chassis_blanking_diode_added: '',
        chassis_blanking_inductor: '',
        chassis_blanking_inductor_number: '',
        chassis_r_g_b_diodes_added: '',
        mux_termination_resistors: '',
        mux_rgb_input_resistors: '',
        mux_rgb_input_capacitors: '',
        mux_blanking_ground_resistor: '',
        mux_blanking_inline_resistor: '',
        mux_audio_resistors: '',
        mux_blanking_diode: '',
        analog_chroma_vpp: '',
        mux_transistor: '',
        mux_transistor_base_resistor: '',
        confirmed: false
      });
      setShowRgbMuxSection(false);
      setSelectedCrtModel(null);
      setModelInput('');
      setSuggestions([]);
      
      // Fetch fresh attributes when modal opens
      fetchAttributes();
      fetchModBoards();
    }
  }, [show]);

  // Fetch attributes directly from the endpoint
  const fetchAttributes = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/crtattributes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch attributes');
      }
      
      const data = await response.json();
      console.log("Fetched attributes:", data);
      
      // Convert each attribute's possible values into { label, value } for react-select
      const newOptions = {};
      Object.keys(data).forEach(key => {
        newOptions[key] = data[key]?.values?.map(v => ({ label: v, value: v })) || [];
      });
      
      console.log("Converted options:", newOptions);
      setOptions(newOptions);
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error('Failed to load attributes');
    }
  };

  // Fetch available mod boards
  const fetchModBoards = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/mod-boards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch mod boards');
      }
      
      const data = await response.json();
      setAvailableModBoards(data);
    } catch (error) {
      console.error("Error fetching mod boards:", error);
      toast.error('Failed to load mod boards');
    }
  };

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch CRT suggestions with debounce
  const fetchCrtSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setIsLoadingModels(true);
    try {
      const response = await fetch(`${STORE_API_URL}/crts/autocomplete?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch CRT suggestions');
      }
      
      const data = await response.json();
      const models = data.suggestions.map(crt => ({
        value: crt._id,
        label: crt.title || '(Untitled CRT)',
        _id: crt._id,
        slug: crt.slug
      }));
      
      setSuggestions(models);
    } catch (error) {
      console.error('Error fetching CRT suggestions:', error);
    } finally {
      setIsLoadingModels(false);
    }
  };
  
  // Create debounced version of fetchCrtSuggestions
  const debouncedFetch = useCallback(debounce(fetchCrtSuggestions, 300), [token]);
  
  // Handle CRT model selection
  const handleCrtModelChange = async (selected) => {
    setSelectedCrtModel(selected);
    
    if (!selected || !selected._id) {
      return;
    }
    
    try {
      const response = await fetch(`${STORE_API_URL}/crts/${selected._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch CRT details');
      }
      
      const crt = await response.json();
      console.log("CRT data loaded:", crt);
      
      // Pre-populate form with CRT data
      const newFormData = { ...formData };
      
      // Set modifiability fields
      if (crt.rgb_modifiable) {
        newFormData.rgb_modifiable = crt.rgb_modifiable;
      }
      if (crt.component_modifiable) {
        newFormData.component_modifiable = crt.component_modifiable;
      }
      if (crt.svideo_modifiable) {
        newFormData.svideo_modifiable = crt.svideo_modifiable;
      }
      if (crt.composite_modifiable) {
        newFormData.composite_modifiable = crt.composite_modifiable;
      }
      
      // Set multi-select fields - ensure they're always arrays
      newFormData.chassis = Array.isArray(crt.chassis) ? crt.chassis : 
                           (crt.chassis ? [crt.chassis] : []);
      
      newFormData.chroma = Array.isArray(crt.chroma) ? crt.chroma : 
                          (crt.chroma ? [crt.chroma] : []);
      
      newFormData.osd = Array.isArray(crt.osd) ? crt.osd : 
                        (crt.osd ? [crt.osd] : []);
      
      // Set mod boards - convert to array of IDs
      newFormData.mod_boards = Array.isArray(crt.mod_boards) ? 
                               crt.mod_boards.map(board => typeof board === 'string' ? board : board._id) : 
                               [];
      
      // Set mods field
      if (crt.mods) {
        newFormData.mods = crt.mods;
      }
      
      // Set manual links if available
      if (crt.manual_links) {
        newFormData.manual_links_data = crt.manual_links;
      }
      
      // Set shared documents if available
      if (crt.shared_documents && Array.isArray(crt.shared_documents) && crt.shared_documents.length > 0) {
        newFormData.shared_documents_data = crt.shared_documents;
      }
      
      // Set tubes if available
      if (crt.tubes && Array.isArray(crt.tubes) && crt.tubes.length > 0) {
        newFormData.tubes_data = crt.tubes;
      }
      
      // Set RGB mod fields
      if (crt.rgb_mod_link) {
        newFormData.rgb_mod_link = crt.rgb_mod_link;
      }
      if (crt.rgb_mod_notes) {
        newFormData.rgb_mod_notes = crt.rgb_mod_notes;
      }
      
      // Set inductor fields
      if (crt.chassis_blanking_inductor) {
        newFormData.chassis_blanking_inductor = crt.chassis_blanking_inductor;
      }
      if (crt.chassis_blanking_inductor_number) {
        newFormData.chassis_blanking_inductor_number = crt.chassis_blanking_inductor_number;
      }
      
      // Set RGB MUX calculator fields
      if (crt.rgb_mux_calculator_enabled) {
        newFormData.rgb_mux_calculator_enabled = crt.rgb_mux_calculator_enabled;
        setShowRgbMuxSection(crt.rgb_mux_calculator_enabled === 'true');
      }
      
      // Set all the RGB MUX fields if they exist
      [
        'rgb_direct_injection',
        'chassis_stereo',
        'chassis_inline_resistors',
        'chassis_ground_resistors',
        'chassis_blanking_diode',
        'chassis_r_g_b_diodes',
        'chassis_inline_jungle_pin_numbers',
        'chassis_inline_osd_pin_numbers',
        'chassis_inline_resistor_numbers',
        'chassis_ground_resistor_numbers',
        'chassis_inline_blanking_resistor_value',
        'chassis_blanking_ground_resistor_value',
        'chassis_capacitor_numbers',
        'chassis_capacitor_value',
        'chassis_change_caps_label',
        'chassis_blanking_diode_position_before_ground',
        'chassis_blanking_diode_added',
        'chassis_r_g_b_diodes_added',
        'mux_termination_resistors',
        'mux_rgb_input_resistors',
        'mux_rgb_input_capacitors',
        'mux_blanking_ground_resistor',
        'mux_blanking_inline_resistor',
        'mux_audio_resistors',
        'mux_blanking_diode',
        'analog_chroma_vpp',
        'mux_transistor',
        'mux_transistor_base_resistor'
      ].forEach(field => {
        if (crt[field]) {
          newFormData[field] = crt[field];
        }
      });
      
      console.log("Form data updated:", newFormData);
      setFormData(newFormData);
      toast.success(`Loaded settings from ${crt.title}`);
      
    } catch (error) {
      toast.error('Failed to load CRT details');
      console.error(error);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selected) => {
    // For multi-select fields
    if (Array.isArray(selected)) {
      const values = selected.map(option => option.value);
      setFormData(prev => ({ ...prev, [name]: values }));
    } 
    // For single-select fields
    else if (selected) {
      setFormData(prev => ({ ...prev, [name]: selected.value }));
    } 
    // For cleared fields
    else {
      setFormData(prev => ({ ...prev, [name]: Array.isArray(prev[name]) ? [] : '' }));
    }
  };

  const handleToggleChange = (name, checked) => {
    setFormData(prev => ({ 
      ...prev, 
      [name]: checked ? 'true' : 'false' 
    }));
  };

  const handleSubmit = async () => {
    if (!formData.confirmed) {
      toast.error('Please confirm that you understand existing values will be overwritten.');
      return;
    }

    try {
      // Prepare data object - only include fields that have values
      const updateData = {};
      Object.keys(formData).forEach(key => {
        // Skip the confirmation field and data fields
        if (key === 'confirmed' || key.endsWith('_data')) return;
        
        // Handle copying fields specially
        if (key === 'manual_links' && formData[key] && formData.manual_links_data) {
          updateData.manual_links = formData.manual_links_data;
          return;
        }
        
        if (key === 'shared_documents' && formData[key] && formData.shared_documents_data) {
          updateData.shared_documents = formData.shared_documents_data;
          return;
        }
        
        if (key === 'tubes' && formData[key] && formData.tubes_data) {
          updateData.tubes = formData.tubes_data;
          return;
        }
        
        // Only include fields that have values
        if (Array.isArray(formData[key])) {
          if (formData[key].length > 0) {
            updateData[key] = formData[key];
          }
        } else if (formData[key]) {
          updateData[key] = formData[key];
        }
      });

      // If no fields to update, show error
      if (Object.keys(updateData).length === 0) {
        toast.error('Please select at least one field to update.');
        return;
      }

      // Show confirmation toast with count of CRTs being updated
      toast.info(`Updating ${selectedCRTs.length} CRTs...`);

      // Call API to update all selected CRTs
      const response = await fetch(`${STORE_API_URL}/crts/batch-update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          crtIds: selectedCRTs.map(crt => crt._id),
          updateData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update CRTs');
      }

      const result = await response.json();
      toast.success(`Successfully updated ${result.updatedCount} CRTs`);
      
      // Close modal and refresh data
      onHide();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to update CRTs');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Edit {selectedCRTs.length} CRTs</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          <Alert.Heading>Warning: This will overwrite existing values</Alert.Heading>
          <p>
            You are about to modify {selectedCRTs.length} CRTs. Any values you set here will 
            overwrite the existing values for all selected CRTs. Fields left blank will not be changed.
          </p>
          <p>
            Selected CRTs: {selectedCRTs.map(crt => crt.title).join(', ')}
          </p>
        </Alert>

        {/* CRT Model Search - to pre-populate fields */}
        <div className="form-group mb-4">
          <CreatableSelect
            isClearable
            styles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              })
            }}
            isSearchable
            isLoading={isLoadingModels}
            options={suggestions}
            value={selectedCrtModel}
            onInputChange={(inputValue, { action }) => {
              if (action === 'input-change') {
                setModelInput(inputValue);
                debouncedFetch(inputValue);
              }
              if (action === 'menu-close') {
                setModelInput('');
              }
            }}
            inputValue={modelInput}
            onChange={handleCrtModelChange}
            placeholder="Search to copy values from existing CRT model"
            classNamePrefix="dark-select"
            noOptionsMessage={() => "Type to search for CRT models"}
          />
        </div>

        <h5>Modifiability</h5>
        <div className="row mb-3">
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>RGB Modifiable</Form.Label>
              <Form.Select 
                value={formData.rgb_modifiable}
                onChange={(e) => handleChange('rgb_modifiable', e.target.value)}
              >
                <option value="">No Change</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Component Modifiable</Form.Label>
              <Form.Select 
                value={formData.component_modifiable}
                onChange={(e) => handleChange('component_modifiable', e.target.value)}
              >
                <option value="">No Change</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>S-Video Modifiable</Form.Label>
              <Form.Select 
                value={formData.svideo_modifiable}
                onChange={(e) => handleChange('svideo_modifiable', e.target.value)}
              >
                <option value="">No Change</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label>Composite Modifiable</Form.Label>
              <Form.Select 
                value={formData.composite_modifiable}
                onChange={(e) => handleChange('composite_modifiable', e.target.value)}
              >
                <option value="">No Change</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </Form.Select>
            </Form.Group>
          </div>
        </div>

        <h5>Technical Information</h5>
        <div className="row mb-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Chassis</Form.Label>
              <CreatableSelect
                isMulti
                placeholder="Select chassis..."
                options={options.chassis || []}
                value={(formData.chassis || []).map(c => ({ label: c, value: c }))}
                onChange={(selected) => handleSelectChange('chassis', selected)}
                classNamePrefix="dark-select"
                isClearable
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Mods</Form.Label>
              <Form.Control
                type="text"
                value={formData.mods}
                onChange={(e) => handleChange('mods', e.target.value)}
                placeholder="Common mods for these CRTs"
              />
            </Form.Group>
          </div>
        </div>

        <div className="row mb-3">
          <Form.Group>
            <Form.Label>Copy Options</Form.Label>
            <div className="row">
              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  id="copy-gallery-images"
                  label="Copy Gallery Images"
                  checked={formData.gallery_images}
                  onChange={(e) => handleChange('gallery_images', e.target.checked)}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="copy-manual-links"
                  label="Copy Manual Links"
                  checked={formData.manual_links}
                  onChange={(e) => handleChange('manual_links', e.target.checked)}
                  className="mb-2"
                />
              </div>
              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  id="copy-shared-documents"
                  label="Copy Shared Documents"
                  checked={formData.shared_documents}
                  onChange={(e) => handleChange('shared_documents', e.target.checked)}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="copy-tubes"
                  label="Copy Tubes"
                  checked={formData.tubes}
                  onChange={(e) => handleChange('tubes', e.target.checked)}
                />
              </div>
            </div>
          </Form.Group>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Chroma IC</Form.Label>
              <CreatableSelect
                isMulti
                placeholder="Select chroma IC..."
                options={options.chroma || []}
                value={(formData.chroma || []).map(c => ({ label: c, value: c }))}
                onChange={(selected) => handleSelectChange('chroma', selected)}
                classNamePrefix="dark-select"
                isClearable
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>OSD IC</Form.Label>
              <CreatableSelect
                isMulti
                placeholder="Select OSD IC..."
                options={options.osd || []}
                value={(formData.osd || []).map(c => ({ label: c, value: c }))}
                onChange={(selected) => handleSelectChange('osd', selected)}
                classNamePrefix="dark-select"
                isClearable
              />
            </Form.Group>
          </div>
        </div>

        <h5>Compatible Mod Boards</h5>
        <div className="row mb-3">
          <div className="col-md-12">
            <Form.Group>
              <Form.Label>Mod Boards</Form.Label>
              <CreatableSelect
                isMulti
                placeholder="Select compatible mod boards"
                options={availableModBoards.map(board => ({
                  value: board._id,
                  label: `${board.name}${board.category ? ` (${board.category})` : ''}`,
                  board: board
                }))}
                value={(formData.mod_boards || []).map(boardId => {
                  const foundBoard = availableModBoards.find(board => board._id === boardId);
                  if (foundBoard) {
                    return {
                      value: foundBoard._id,
                      label: `${foundBoard.name}${foundBoard.category ? ` (${foundBoard.category})` : ''}`,
                      board: foundBoard
                    };
                  }
                  return { value: boardId, label: boardId };
                })}
                onChange={(selected) => {
                  const selectedIds = (selected || []).map(option => option.value);
                  setFormData(prev => ({ ...prev, mod_boards: selectedIds }));
                }}
                classNamePrefix="dark-select"
                isClearable
              />
              {availableModBoards.length === 0 && (
                <small className="text-muted">
                  No mod boards available. Manage mod boards in SuperAdmin.
                </small>
              )}
            </Form.Group>
          </div>
        </div>

        <h5>RGB Mod Information</h5>
        <div className="row mb-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>RGB Mod Link</Form.Label>
              <Form.Control
                type="url"
                value={formData.rgb_mod_link || ''}
                onChange={(e) => handleChange('rgb_mod_link', e.target.value)}
                placeholder="https://example.com/rgb-mod-guide"
              />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>RGB Mod Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.rgb_mod_notes || ''}
                onChange={(e) => handleChange('rgb_mod_notes', e.target.value)}
                placeholder="Additional notes about the RGB mod..."
              />
            </Form.Group>
          </div>
        </div>

        <h5>RGB MUX Calculator Settings</h5>
        {/* RGB MUX Calculator Section */}
        <div className="form-group">
          <div style={{width:'100%'}}>
            <Form.Check 
              type="switch"
              id="rgb-mux-toggle"
              label=""
              checked={formData.rgb_mux_calculator_enabled === 'true'}
              onChange={(e) => {
                handleToggleChange('rgb_mux_calculator_enabled', e.target.checked);
                setShowRgbMuxSection(e.target.checked);
              }}
              className="mb-3"
            />
          </div>
        </div>

        {(formData.rgb_mux_calculator_enabled === 'true' || showRgbMuxSection) && (
          <>
            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>RGB Direct Injection</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="rgb_direct_injection-toggle"
                      checked={formData.rgb_direct_injection === 'true'}
                      onChange={(e) => handleToggleChange('rgb_direct_injection', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Stereo</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_stereo-toggle"
                      checked={formData.chassis_stereo === 'true'}
                      onChange={(e) => handleToggleChange('chassis_stereo', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B Diodes</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_r_g_b_diodes-toggle"
                      checked={formData.chassis_r_g_b_diodes === 'true'}
                      onChange={(e) => handleToggleChange('chassis_r_g_b_diodes', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Blanking Diode</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_blanking_diode-toggle"
                      checked={formData.chassis_blanking_diode === 'true'}
                      onChange={(e) => handleToggleChange('chassis_blanking_diode', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B Diodes added?</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_r_g_b_diodes_added-toggle"
                      checked={formData.chassis_r_g_b_diodes_added === 'true'}
                      onChange={(e) => handleToggleChange('chassis_r_g_b_diodes_added', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Blanking Diode added?</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_blanking_diode_added-toggle"
                      checked={formData.chassis_blanking_diode_added === 'true'}
                      onChange={(e) => handleToggleChange('chassis_blanking_diode_added', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Blanking Diode Before Chassis Ground?</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check
                      type="switch"
                      id="chassis_blanking_diode_position_before_ground-toggle"
                      checked={formData.chassis_blanking_diode_position_before_ground === 'true'}
                      onChange={(e) => handleToggleChange('chassis_blanking_diode_position_before_ground', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Change Inline Caps?</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check
                      type="switch"
                      id="chassis_change_caps_label-toggle"
                      checked={formData.chassis_change_caps_label === 'true'}
                      onChange={(e) => handleToggleChange('chassis_change_caps_label', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B, Blanking Inline Jungle PIN Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_inline_jungle_pin_numbers}
                    onChange={(e) => handleChange('chassis_inline_jungle_pin_numbers', e.target.value)}
                    placeholder="10,11,12,13"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B, Blanking Inline OSD PIN Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_inline_osd_pin_numbers}
                    onChange={(e) => handleChange('chassis_inline_osd_pin_numbers', e.target.value)}
                    placeholder="14,15,16,17"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B Capacitor Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_capacitor_numbers}
                    onChange={(e) => handleChange('chassis_capacitor_numbers', e.target.value)}
                    placeholder="C1, C2, C3"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B Capacitor Value (uF)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_capacitor_value}
                    onChange={(e) => handleChange('chassis_capacitor_value', e.target.value)}
                    placeholder="0.1"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B, Blanking Inline Resistor Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_inline_resistor_numbers}
                    onChange={(e) => handleChange('chassis_inline_resistor_numbers', e.target.value)}
                    placeholder="R201, R202, R203, R208"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Inline Resistors Values (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_inline_resistors}
                    onChange={(e) => handleChange('chassis_inline_resistors', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Inline Blanking Resistor Value (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_inline_blanking_resistor_value}
                    onChange={(e) => handleChange('chassis_inline_blanking_resistor_value', e.target.value)}
                    placeholder="2500"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis R, G, B, Blanking Ground Resistor Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_ground_resistor_numbers}
                    onChange={(e) => handleChange('chassis_ground_resistor_numbers', e.target.value)}
                    placeholder="R204, R205, R206, R207"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Ground Resistors (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_ground_resistors}
                    onChange={(e) => handleChange('chassis_ground_resistors', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Chassis Blanking Ground Resistor Value (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_blanking_ground_resistor_value}
                    onChange={(e) => handleChange('chassis_blanking_ground_resistor_value', e.target.value)}
                    placeholder="2500"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Inductor</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis_blanking_inductor-toggle"
                      checked={formData.chassis_blanking_inductor === 'true'}
                      onChange={(e) => handleToggleChange('chassis_blanking_inductor', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Inductor Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chassis_blanking_inductor_number}
                    onChange={(e) => handleChange('chassis_blanking_inductor_number', e.target.value)}
                    placeholder="L003"
                  />
                </Form.Group>
              </div>
            </div>
            <h5>MUX Board Configuration</h5>
            
            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Termination - R1, R2, R3 (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mux_termination_resistors}
                    onChange={(e) => handleChange('mux_termination_resistors', e.target.value)}
                    placeholder="75"
                  />
                </Form.Group>
              </div>
              {formData.rgb_direct_injection === 'true' ? (
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>RGB Input Caps - R4, R5, R6 (µF)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.mux_rgb_input_capacitors}
                      onChange={(e) => handleChange('mux_rgb_input_capacitors', e.target.value)}
                      placeholder="0.1"
                    />
                  </Form.Group>
                </div>
              ) : (
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>RGB Input - R4, R5, R6 (Ω)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.mux_rgb_input_resistors}
                      onChange={(e) => handleChange('mux_rgb_input_resistors', e.target.value)}
                      placeholder="1000"
                    />
                  </Form.Group>
                </div>
              )}
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Audio - R7, R8 (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mux_audio_resistors}
                    onChange={(e) => handleChange('mux_audio_resistors', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Blanking Diode - D9</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="mux_blanking_diode-toggle"
                      checked={formData.mux_blanking_diode === 'true'}
                      onChange={(e) => handleToggleChange('mux_blanking_diode', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Blanking Ground - R10 (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mux_blanking_ground_resistor}
                    onChange={(e) => handleChange('mux_blanking_ground_resistor', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Blanking Inline - R11 (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mux_blanking_inline_resistor}
                    onChange={(e) => handleChange('mux_blanking_inline_resistor', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Transistor - Q1</Form.Label>
                  <div style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="mux_transistor-toggle"
                      checked={formData.mux_transistor === 'true'}
                      onChange={(e) => handleToggleChange('mux_transistor', e.target.checked)}
                    />
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Transistor Base - R12 (Ω)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mux_transistor_base_resistor}
                    onChange={(e) => handleChange('mux_transistor_base_resistor', e.target.value)}
                    placeholder="1000"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Analog Chroma Vp-p</Form.Label>
                  <Form.Select
                    value={formData.analog_chroma_vpp}
                    onChange={(e) => handleChange('analog_chroma_vpp', e.target.value)}
                  >
                    <option value="">No Change</option>
                    <option value="0.5">0.5V</option>
                    <option value="0.7">0.7V</option>
                    <option value="1.0">1.0V</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </>
        )}

        <Form.Group className="mt-4">
          <Form.Check
            type="checkbox"
            id="confirm-checkbox"
            label="I understand that this will overwrite existing values for all selected CRTs"
            checked={formData.confirmed}
            onChange={(e) => handleChange('confirmed', e.target.checked)}
            className="text-danger"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={!formData.confirmed}
        >
          Apply Changes to {selectedCRTs.length} CRTs
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkEditModal; 